//-- Copyright (c) 2021 TMT Insights Group, LLC
import React from 'react';
import { TitleEntity, UserProfile } from '@/redux/slices/slice_types';
import { AxiosResponse } from 'axios';
import { cloneDeep } from 'lodash';
import { useHistory } from 'react-router-dom';
import axios from '@/_utils/axios';
import { FileFormat, SUPPORTED_SUBTITLE_AND_OTHER_PREVIEW_FORMATS, UNSUPPORTED_PREVIEW_FORMATS } from '@/constants/constants';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';

export { useHistory, axios };

// this seems like it would be problematic, but i'm yet to find an issue
export const deepCopy = (obj: any): any => cloneDeep(obj);

// does NOT merge arrays, only objects; arrays from base are replaced by arrays from updates
export const deepMerge = (base: any, updates: any): any => {
  const copyObj1 = deepCopy(base);
  const copyObj2 = deepCopy(updates);
  const output = { ...copyObj1 };

  for (const key in copyObj2) {
    if (Array.isArray(copyObj2[key])) {
      output[key] = [...copyObj2[key]];
    } else if (typeof copyObj2[key] === 'object' && copyObj2[key] !== null && copyObj1[key]) {
      output[key] = deepMerge(copyObj1[key], copyObj2[key]);
    } else {
      output[key] = copyObj2[key];
    }
  }

  return output;
};

// promise that resolves if an image source url is valid
export const checkIfUrlIsValid = async (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
};

// should return false for values: false, null, undefined, '', []
// should return true for 0
export const isFalsey = (value: any): boolean => {
  return value === false || value == null || value === '' || (Array.isArray(value) && value.length === 0);
};

export const getRandomFromArray = (array: any[]) => array[Math.floor(Math.random() * array.length)];

export const isOverflown = ({ clientWidth, scrollWidth }: { clientWidth: number; scrollWidth: number }) => scrollWidth > clientWidth;

export const convertRemToPixels = (rem: number) => {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
};

export const getInitials = (user: UserProfile) => {
  const { first_name, last_name } = user;
  const names = first_name && first_name + last_name && last_name;
  let initials = '';

  if (first_name && first_name.length > 0 && last_name && last_name.length > 0) {
    initials += first_name[0];
    initials += last_name[0];
  } else if ((names as string).length > 0) {
    initials += (names as string).substring(0, 2);
  } else {
    initials += 'x';
  }
  return initials.toUpperCase();
};

// initially we were saving more fields to the columns
// the purpose of this is to clear out those unnecessary and problematic saved fields
export const cleanColumnPreferences = (preferences: any) => {
  const prefs = deepCopy(preferences);
  Object.keys(prefs).forEach((key) => {
    if (prefs[key]?.columns) {
      prefs[key].columns = prefs[key].columns.map((col: any) => {
        return {
          id: col.id,
          order: col.order,
          hidden: col.hidden
        };
      });
    }
  });
  return prefs;
};

// if the environment you're in is not of a secure origin (https:// or localhost)
// nagigator.clipboard.writeText will throw an error
// so we are disabling it if navigator.clipboard is not available
export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea');
  el.value = str;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  navigator.clipboard.writeText(str);
  document.body.removeChild(el);
};

export const insertWordBreaks = (str: string, splitAt: string = '_'): React.ReactNode | null => {
  if (typeof str !== 'string') {
    return null;
  }

  const parts = str.split(splitAt);
  const result: React.ReactNode[] = [];

  for (let i = 0; i < parts.length; i++) {
    result.push(parts[i]);
    if (i < parts.length - 1) {
      const el = React.createElement('wbr', { key: i });
      result.push(el);
      result.push(splitAt);
    }
  }

  return result.length === 1 ? result[0] : result;
};

export const storeFile = (response: any, filename: string) => {
  const blob = new Blob([response.data], { type: 'application/ms-excel' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const exportStringAsJsonFile = (filename: string, jsonString: string) => {
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.json`;
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const getDuration = (seconds: number) => {
  if (!seconds) return null;
  return new Date(seconds * 1000).toISOString().substring(11, 16);
};

export const colorConsoleLog = (message: string, color: string = 'magenta') => {
  // eslint-disable-next-line no-console
  return console.log(`%c${message}`, `color:${color}`);
};

export const cleanEmptyKeys = (obj: any | any[]): any | any[] => {
  if (Array.isArray(obj)) {
    return obj.map((v) => (v && typeof v === 'object' ? cleanEmptyKeys(v) : v)).filter((v) => !(v == null || v.length < 0));
  } else {
    return Object.entries(obj)
      .map(([k, v]) => [k, v && typeof v === 'object' ? cleanEmptyKeys(v) : v])
      .reduce((a: any, [k, v]) => (v == null ? a : ((a[k] = v), a)), {});
  }
};

export const alphabetizeByKey = (unorderedObj: any) =>
  Object.keys(unorderedObj)
    .sort()
    .reduce((obj: any, key: string) => {
      obj[key] = unorderedObj[key];
      return obj;
    }, {});

export const handleReport = (response: AxiosResponse<any>) => {
  const blob = new Blob([response.data], { type: 'application/ms-excel' });
  const attachment = response.headers['content-disposition'];
  const filename = attachment?.replace('attachment: filename=', '') || 'report.xlsx';
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const getUserDisplayName = (user: Partial<UserProfile>) => {
  if (!user) return null;
  const { first_name, last_name } = user;
  if (user.first_name || user.last_name) {
    return `${first_name} ${last_name}`;
  } else return user.email || user.username || user.initials;
};

export const validEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

(Object as any).byString = (o: any, s: any) => {
  if (!s) return null;
  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, ''); // strip a leading dot
  const a = s.split('.');
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (o === Object(o) && k in o) {
      o = o[k];
    } else {
      return;
    }
  }
  return o;
};

type LineageMetaTitle = Pick<TitleEntity | TitleEntityV2, 'lineage_meta' | 'name' | 'entity_type' | 'og_entity_type' | 'sequence_order'> | undefined;

export const getLineageMetaTitle = (titleEntity: LineageMetaTitle, suppressEpisodeName: boolean = false): string => {
  if (!titleEntity || !titleEntity.lineage_meta || !Object.keys(titleEntity.lineage_meta).length) {
    return '';
  }

  const meta = titleEntity.lineage_meta;
  const isStandalone = meta.standalone_name;
  if (isStandalone) return isStandalone;
  const season_sequence_order = meta.season_sequence_order;
  const isEpisode =
    ['program', 'episode'].includes(titleEntity.og_entity_type || titleEntity.entity_type) || meta.program_sequence_order || meta.episode_name;
  const isMovie = meta.movie_name;
  const isOVA = meta.ova_name;
  const epNum = meta.program_sequence_order || titleEntity.sequence_order || meta.episode_sequence_order;
  const epNumDisplay = isEpisode && epNum ? `E${isNaN(Number(epNum)) ? epNum : Number(epNum)}` : '';
  const episodeName = meta.episode_name || meta.program_name || titleEntity.name;
  const seriesName = meta.series_name || '';
  const seasonNum = season_sequence_order ? `S${season_sequence_order}${epNumDisplay ? ' ' : ''}` : '';

  if (isEpisode) {
    const episodeTitle = suppressEpisodeName ? '' : `: ${episodeName}`;
    return `${seriesName} ${seasonNum}${epNumDisplay}${episodeTitle}`;
  } else if (isMovie) {
    return seriesName ? `${seriesName} ${meta.movie_name}` : meta.movie_name;
  } else if (isOVA) {
    return seriesName ? `${seriesName} ${meta.ova_name}` : meta.ova_name;
  } else {
    const seriesTitle = seriesName ? `${seasonNum?.length || epNumDisplay?.length ? ': ' : ''}${seriesName}` : '';
    return `${seasonNum}${epNumDisplay}${seriesTitle}`;
  }
};

export const deleteObjectKeysByString = (obj: any, word: string) => {
  return Object.keys(obj).forEach((key) => {
    if (obj[key] === word) {
      delete obj[key];
    }
  });
};

export function convertStylesToStyled(styles: any): string {
  if (!styles) return '';
  let styledString = '';

  for (const [key, value] of Object.entries(styles)) {
    if (typeof value === 'object' && !Array.isArray(value)) {
      styledString += `${key} {${convertStylesToStyled(value)}}`;
    } else {
      const processedValue = typeof value === 'number' ? `${value}px` : value;
      styledString += `${kebabCase(key)}: ${processedValue};\n`;
    }
  }

  return styledString;
}

export function convertToKeyVal(arr: any[]) {
  if (!arr) return [];
  return arr.reduce((memo, item) => ({ ...memo, [item[0]]: item[1] }), {});
}

export const getUnsupportedPreviewFormats = (other: FileFormat[]) => {
  const disabledOtherTypes = other.filter((type) => !SUPPORTED_SUBTITLE_AND_OTHER_PREVIEW_FORMATS.includes(type));
  return [...disabledOtherTypes, ...UNSUPPORTED_PREVIEW_FORMATS];
};

export function isArrayTheSame(arr1: unknown[], arr2: unknown[]) {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((element, index) => element === arr2[index]);
}

export function isVersion(data: any): data is TitleEntity {
  return 'type' in data && data.type === 'version';
}

export function isTitleEntity(data: any): data is TitleEntity {
  return 'entity_type' in data && data.entity_type !== 'version' && 'children' in data;
}

export function isTitleEntityV2(data: any): data is TitleEntityV2 {
  return 'entity_type' in data && data.entity_type !== 'version' && 'relationships' in data;
}

export function getNestedValue(path: string, obj: any): any {
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[key];
  }

  return current;
}

export const displayBulkActionIdent = (ident: string) => {
  const newStr = ident
    .split('_')
    .filter((s) => ['orders', 'jobs', 'tasks', 'result'].indexOf(s.toLowerCase()) === -1)
    .join('_');

  return titleCase(convertFromSnakeCase(newStr)).replace('Qc', 'QC');
};

export const convertFromSnakeCase = (str: string) => {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatBytes = (bytes: number = 0, decimals: number = 2): string => {
  if (!bytes || bytes === 0) return '? b';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['b', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const formattedNumber = (bytes / Math.pow(k, i)).toFixed(dm);
  const numberWithCommas = parseFloat(formattedNumber).toLocaleString();
  return numberWithCommas + ' ' + sizes[i];
};

export const kebabCase = (str: string): string => str.replace(/[A-Z]/g, (v) => `-${v.toLowerCase()}`);

export function pluralizeNoun(noun: string): string {
  if (noun.endsWith('y') && !/[aeiou]y$/.test(noun)) {
    return noun.slice(0, -1) + 'ies';
  } else if (noun.endsWith('s') || noun.endsWith('sh') || noun.endsWith('ch') || noun.endsWith('x') || noun.endsWith('z')) {
    return noun + 'es';
  } else {
    return noun + 's';
  }
}

export function capitalizeNoun(noun: string): string {
  if (typeof noun !== 'string' || noun.length === 0) return '';
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

export const titleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '...';
}

export const joinWithAnd = (arr: string[]) => {
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return arr.join(' and ');
  return arr.slice(0, -1).join(', ') + ', and ' + arr[arr.length - 1];
};

/**
 * Creates a debounced async function that delays execution until after `wait` milliseconds
 * have passed since the last call.
 *
 * Each call returns a Promise that resolves or rejects with the result of the single
 * underlying async invocation using the latest arguments provided.
 */
export function debounceAsync<TArgs extends unknown[], TResult>(fn: (...args: TArgs) => Promise<TResult>, wait: number) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingResolves: Array<(value: TResult) => void> = [];
  let pendingRejects: Array<(reason?: unknown) => void> = [];
  let lastArgs: TArgs;

  return (...args: TArgs): Promise<TResult> => {
    lastArgs = args;

    if (timer) {
      clearTimeout(timer);
    }

    return new Promise<TResult>((resolve, reject) => {
      pendingResolves.push(resolve);
      pendingRejects.push(reject);

      timer = setTimeout(async () => {
        const resolves = pendingResolves;
        const rejects = pendingRejects;

        pendingResolves = [];
        pendingRejects = [];
        timer = null;

        try {
          const result = await fn(...lastArgs);
          resolves.forEach((r) => r(result));
        } catch (error) {
          rejects.forEach((r) => r(error));
        }
      }, wait);
    });
  };
}

export function deepEqual(a: unknown, b: unknown): boolean {
  return normalizeForComparison(a) === normalizeForComparison(b);
}

function normalizeForComparison(value: unknown): string {
  if (value === null) {
    return 'null';
  }

  if (value === undefined) {
    return 'undefined';
  }

  if (typeof value !== 'object') {
    if (typeof value === 'number' && Number.isNaN(value)) {
      return 'NaN';
    }

    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    const normalizedItems = value.map(normalizeForComparison).sort();
    return `[${normalizedItems.join(',')}]`;
  }

  const obj = value as Record<string, unknown>;
  const normalizedEntries = Object.keys(obj)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${normalizeForComparison(obj[key])}`);

  return `{${normalizedEntries.join(',')}}`;
}
